import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  StormTerritory,
  StormTerritoryFormData,
  StormAlert,
  StormSubscription,
  StormSubscriptionFormData,
  StormStats,
  StormAlertsListResponse,
  StormTerritoriesListResponse,
  StormAlertFilters,
} from '../types/stormtracker';

interface StormStatsApiRow {
  event_type?: string;
  severity?: string;
  count: number;
}

/** Maps Django `/api/storm-alerts/stats/` payload to dashboard `StormStats`. */
function normalizeStormStats(data: Record<string, unknown>): StormStats {
  const byType = (data.by_event_type as StormStatsApiRow[] | undefined) ?? [];
  const bySev = (data.by_severity as StormStatsApiRow[] | undefined) ?? [];
  const alerts_by_type = { hail: 0, wind: 0, tornado: 0 };
  for (const row of byType) {
    const k = row.event_type as keyof typeof alerts_by_type;
    if (k in alerts_by_type) alerts_by_type[k] = row.count;
  }
  const alerts_by_severity = { minor: 0, moderate: 0, severe: 0, extreme: 0 };
  for (const row of bySev) {
    const k = row.severity as keyof typeof alerts_by_severity;
    if (k in alerts_by_severity) alerts_by_severity[k] = row.count;
  }
  return {
    alerts_24h: Number(data.last_24_hours ?? data.alerts_24h ?? 0),
    alerts_7d: Number(data.last_7_days ?? data.alerts_7d ?? 0),
    high_priority_alerts: Number(data.high_opportunity_count ?? data.high_priority_alerts ?? 0),
    active_territories: Number(data.active_territories ?? 0),
    user_subscriptions: Number(data.user_subscriptions ?? 0),
    alerts_by_type,
    alerts_by_severity,
  };
}

// Query keys for cache management
export const stormTrackerKeys = {
  all: ['storm-tracker'] as const,
  territories: () => [...stormTrackerKeys.all, 'territories'] as const,
  territory: (id: number) => [...stormTrackerKeys.territories(), id] as const,
  alerts: () => [...stormTrackerKeys.all, 'alerts'] as const,
  alertsList: (filters?: StormAlertFilters) => [...stormTrackerKeys.alerts(), 'list', filters] as const,
  alert: (id: number) => [...stormTrackerKeys.alerts(), id] as const,
  recentAlerts: () => [...stormTrackerKeys.alerts(), 'recent'] as const,
  subscriptions: () => [...stormTrackerKeys.all, 'subscriptions'] as const,
  subscription: (id: number) => [...stormTrackerKeys.subscriptions(), id] as const,
  stats: () => [...stormTrackerKeys.all, 'stats'] as const,
};

// ============================================================================
// Territory Hooks
// ============================================================================

export function useAutoStormTerritories(user: { id: number; organization?: { id: string } } | null) {
  return useQuery({
    queryKey: [...stormTrackerKeys.territories(), 'automatic', user?.organization?.id, user?.id],
    enabled: !!user,
    queryFn: async () => {
      const territories: StormTerritory[] = [];
      // Filter before pagination so legacy records cannot hide a rep's watchlist.
      // Keep requests on our API rather than following an absolute next URL.
      for (let page = 1; ; page += 1) {
        const response = await api.get<StormTerritory[] | StormTerritoriesListResponse>(
          `/api/storm-territories/?is_auto_generated=true&page=${page}`
        );
        if (Array.isArray(response.data)) return response.data;
        territories.push(...response.data.results);
        if (!response.data.next) return territories;
      }
    },
  });
}

export function useStormTerritories() {
  return useQuery({
    queryKey: stormTrackerKeys.territories(),
    queryFn: async () => {
      const response = await api.get<StormTerritory[] | StormTerritoriesListResponse>('/api/storm-territories/');
      // Handle both array and paginated response
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return response.data.results;
    },
  });
}

export function useStormTerritory(id: number | null) {
  return useQuery({
    queryKey: stormTrackerKeys.territory(id!),
    queryFn: async () => {
      const response = await api.get<StormTerritory>(`/api/storm-territories/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateStormTerritory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StormTerritoryFormData) => {
      const response = await api.post<StormTerritory>('/api/storm-territories/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.territories() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.stats() });
    },
  });
}

export function useUpdateStormTerritory(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<StormTerritoryFormData>) => {
      const response = await api.patch<StormTerritory>(`/api/storm-territories/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.territories() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.territory(id) });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.stats() });
    },
  });
}

export function useDeleteStormTerritory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/storm-territories/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.territories() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.stats() });
    },
  });
}

// ============================================================================
// Subscription Hooks
// ============================================================================

export function useStormSubscriptions() {
  return useQuery({
    queryKey: stormTrackerKeys.subscriptions(),
    queryFn: async () => {
      const response = await api.get<StormSubscription[]>('/api/storm-subscriptions/');
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return (response.data as { results: StormSubscription[] }).results || [];
    },
  });
}

export function useSubscribeToTerritory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (territoryId: number) => {
      const response = await api.post<StormSubscription>(`/api/storm-territories/${territoryId}/subscribe/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.territories() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.stats() });
    },
  });
}

export function useUnsubscribeFromTerritory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (territoryId: number) => {
      await api.post(`/api/storm-territories/${territoryId}/unsubscribe/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.territories() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.stats() });
    },
  });
}

export function useUpdateStormSubscription(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<StormSubscriptionFormData>) => {
      const response = await api.patch<StormSubscription>(`/api/storm-subscriptions/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.subscription(id) });
    },
  });
}

export function useDeleteStormSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/storm-subscriptions/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: stormTrackerKeys.stats() });
    },
  });
}

// ============================================================================
// Alert Hooks
// ============================================================================

export function useStormAlerts(filters?: StormAlertFilters) {
  return useQuery({
    queryKey: stormTrackerKeys.alertsList(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.territory) params.append('territory', String(filters.territory));
      if (filters?.event_type) params.append('event_type', filters.event_type);
      if (filters?.severity) params.append('severity', filters.severity);
      if (filters?.min_score !== undefined) params.append('min_score', String(filters.min_score));
      if (filters?.date_from) params.append('date_from', filters.date_from);
      if (filters?.date_to) params.append('date_to', filters.date_to);
      if (filters?.ordering) params.append('ordering', filters.ordering);

      const queryString = params.toString();
      const url = `/api/storm-alerts/${queryString ? `?${queryString}` : ''}`;
      const response = await api.get<StormAlert[] | StormAlertsListResponse>(url);

      if (Array.isArray(response.data)) {
        return response.data;
      }
      return response.data.results;
    },
  });
}

export function useStormAlert(id: number | null) {
  return useQuery({
    queryKey: stormTrackerKeys.alert(id!),
    queryFn: async () => {
      const response = await api.get<StormAlert>(`/api/storm-alerts/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useRecentStormAlerts() {
  return useQuery({
    queryKey: stormTrackerKeys.recentAlerts(),
    queryFn: async () => {
      const response = await api.get<StormAlert[]>('/api/storm-alerts/recent/');
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return (response.data as { results: StormAlert[] }).results || [];
    },
    staleTime: 60 * 1000, // Cache for 1 minute
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
}

// ============================================================================
// Stats Hook
// ============================================================================

export function useStormStats() {
  return useQuery({
    queryKey: stormTrackerKeys.stats(),
    queryFn: async () => {
      const response = await api.get<Record<string, unknown>>('/api/storm-alerts/stats/');
      return normalizeStormStats(response.data);
    },
    staleTime: 60 * 1000, // Cache for 1 minute
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
}

// ============================================================================
// Test Alert Hook
// ============================================================================

export function useTestAlertOptions(territoryId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...stormTrackerKeys.territory(territoryId), 'test-options'],
    enabled,
    queryFn: async () =>
      (await api.get<{ to_number: string }>(`/api/storm-territories/${territoryId}/test-alert/`))
        .data,
  });
}

export function useSendTestAlert() {
  return useMutation({
    mutationFn: async ({ territoryId, toNumber }: { territoryId: number; toNumber: string }) => {
      const response = await api.post<{ message: string }>(
        `/api/storm-territories/${territoryId}/test-alert/`,
        { to_number: toNumber },
      );
      return response.data;
    },
  });
}

// ============================================================================
// Combined Dashboard Data Hook
// ============================================================================

export function useStormDashboard() {
  const territoriesQuery = useStormTerritories();
  const recentAlertsQuery = useRecentStormAlerts();
  const statsQuery = useStormStats();
  const subscriptionsQuery = useStormSubscriptions();

  const stats = useMemo((): StormStats | undefined => {
    if (!statsQuery.data) return undefined;
    const territories = territoriesQuery.data ?? [];
    const subs = subscriptionsQuery.data ?? [];
    return {
      ...statsQuery.data,
      active_territories: territories.filter((t) => t.is_active).length,
      user_subscriptions: subs.length,
    };
  }, [statsQuery.data, territoriesQuery.data, subscriptionsQuery.data]);

  return {
    territories: territoriesQuery.data ?? [],
    recentAlerts: recentAlertsQuery.data ?? [],
    stats,
    subscriptions: subscriptionsQuery.data ?? [],
    isLoading:
      territoriesQuery.isLoading ||
      recentAlertsQuery.isLoading ||
      statsQuery.isLoading,
    isError:
      territoriesQuery.isError ||
      recentAlertsQuery.isError ||
      statsQuery.isError,
    error: territoriesQuery.error || recentAlertsQuery.error || statsQuery.error,
    refetch: () => {
      territoriesQuery.refetch();
      recentAlertsQuery.refetch();
      statsQuery.refetch();
      subscriptionsQuery.refetch();
    },
  };
}
